<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：会社管理画面
 *
 * 作成者：2021/12/6 ノア）黄
 * 最終修正者：2022/1/10 ノア）黄
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button class="backButton" @click="onBackButtonClick" v-tooltip="getTooltip(tooltipType.back)">
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('master.corporate.viewTitle') }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <el-form
        label-width="120px"
        label-position="left"
        class="tti-form"
        ref="form"
        size="mini"
        :disabled="pageLock"
        :model="corporateMasterData"
        :rules="rules"
      >
        <div class="tti-lr-container">
          <!-- 会社情報 -->
          <div class="left">
            <div class="tti-content-title">
              <span class="contentTitle">{{ $t('master.corporate.corporateInfo') }}</span>
            </div>
            <div class="content">
              <!-- 会社コード -->
              <el-form-item :label="$t('master.corporate.corporateCode')">
                <span>{{ corporateMasterData.companyCode }}</span>
              </el-form-item>
              <!-- 会社名  -->
              <el-form-item :label="$t('master.corporate.corporateName')">
                <span>{{ corporateMasterData.companyName }}</span>
              </el-form-item>
              <!-- ID上限数 -->
              <el-form-item :label="$t('master.corporate.idUpper')">
                <span>{{ corporateMasterData.idUpperLimit }}</span>
              </el-form-item>
              <!-- 端末上限数 -->
              <el-form-item :label="$t('master.corporate.terminalNumber')">
                <span>{{ corporateMasterData.terminalUpperLimit }}</span>
              </el-form-item>
            </div>
          </div>

          <!-- 管理対象名称設定 -->
          <div class="right">
            <div class="tti-content-title">
              <span class="contentTitle">{{ $t('master.corporate.corporateSet') }}</span>
            </div>
            <div class="content">
              <!-- 管理対象表示名 -->
              <el-form-item :label="$t('master.corporate.displayName')" prop="managementTargetShowName">
                <el-input
                  v-model.trim="corporateMasterData.managementTargetShowName"
                  class="input"
                  size="small"
                ></el-input>
              </el-form-item>
            </div>
          </div>
        </div>
      </el-form>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 取消 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onCancelButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.cancel)"
          ><tti-svg-icon icon-name="su_005"></tti-svg-icon>{{ $t('buttons.cancel') }}</el-button
        >
      </div>
      <div class="tti-bottom-right-area">
        <!-- 保存 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onSaveButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.save)"
        >
          <tti-svg-icon icon-name="su_002"></tti-svg-icon>{{ $t('buttons.save') }}
        </el-button>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Ref } from 'vue-property-decorator'
import { Form } from 'element-ui'
import { cloneDeep } from 'lodash'

import { i18n } from '@/locales'
import { getCorporateMasterData, updateCorporateMasterData } from '@/store/apis'
import { CorporateMasterData } from '@/models'
import { RULES, validateService } from '@/service/validateService'
import { localService } from '@/commons/services/localService'
import HelperService from '@/service/helperService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import MST_BASE from '@/views/master/MST_BASE.vue'

/**
 * 会社管理画面
 *
 * @class
 */
@Component({
  name: 'mst-co-mgmt',
  components: {}
})
export default class MST_CO_MGMT extends MST_BASE {
  /**
   * フォームのref
   */
  @Ref('form') readonly formRef!: Form

  /**
   * 画面ID
   */
  pageId = 'MST_CO_MGMT'

  /**
   * 会社マスタ
   */
  corporateMasterData: CorporateMasterData = {
    id: '',
    companyCode: '',
    companyName: '',
    idUpperLimit: '',
    terminalUpperLimit: '',
    managementTargetShowName: '',
    itpConnectValidFlag: ''
  }

  /**
   * 検証ルール
   */
  rules: Form['rules'] = {
    // 管理対象表示名
    managementTargetShowName: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('master.corporate.displayName')]).toString(),
        trigger: 'blur'
      },
      // 20文字以内
      {
        validator: validateService.getValidator([
          {
            rule: RULES.MAXLENGTH,
            number: 20,
            message: i18n.t('vaildate.common.maxlength', [i18n.t('master.corporate.displayName'), 20]).toString()
          }
        ]),
        trigger: 'blur'
      }
    ]
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  mounted(): void {
    this.$log.debug('MST_CO_MGMT > mounted')
    this.fetchCorporateMaster()
    this.checkPageLock()
  }

  /**
   * 会社マスタを取得する
   *
   * @returns {void}
   */
  async fetchCorporateMaster(): Promise<void> {
    try {
      this.corporateMasterData = await getCorporateMasterData()
      this.oldData = cloneDeep(this.corporateMasterData)
    } catch (e) {
      this.$log.debug('MST_CO_MGMT > getCorporateMasterData: e=', e)
      throw e
    }
  }

  /**
   * 取消ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onCancelButtonClick(): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > onCancelButtonClick`)
    if (!this.checkDataChanged()) {
      return
    }

    const confirmCallback = () => {
      this.corporateMasterData = cloneDeep(this.oldData)
      this.formRef.clearValidate()
    }

    msgBoxService.confirm(i18n.t('messages.masterCommon.cancelConfirm').toString(), confirmCallback)
  }

  /**
   * 保存前に、チェックを行う
   *
   * @returns {Promise<boolean>} チェック結果
   */
  async saveTableDataBefore(): Promise<boolean> {
    const valid = await this.formRef.validate()
    return valid
  }

  /**
   * 保存API
   *
   * @returns {void}
   */
  async saveTableDataAPI(): Promise<void> {
    const params = {
      id: this.corporateMasterData.id,
      managementTargetShowName: this.corporateMasterData.managementTargetShowName
    }

    await updateCorporateMasterData(params)
  }

  /**
   * 保存後に実行したい操作。
   *
   * @returns {void}
   */
  async saveTableDataAfter(): Promise<void> {
    msgBoxService.info(i18n.t('messages.masterCommon.dataSaved').toString())
    await this.fetchCorporateMaster()
    localService.userInfo = {
      ...localService.userInfo,
      managementTargetShowName: this.corporateMasterData.managementTargetShowName
    }
  }

  /**
   * データが変更されましたかをチェックする。
   *
   * @returns {boolean} isChanged
   */
  checkDataChanged(): boolean {
    return HelperService.isObjectDataChanged(this.oldData, this.corporateMasterData)
  }
}
</script>

<style lang="scss" scoped>
::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

::v-deep .input {
  width: 360px;
}
</style>
